import { tallyTokens } from "../../theme.tsx";
import React from "react";
import { Box, IconButton } from "@mui/material";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import TallyMark from "../../assets/tally-brown.png";
import OpenDrawerIcon from "../icons/OpenDrawerIcon.tsx";
import ConversationActivityDot from "./ConversationActivityDot.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import {
  getHasReadyConversation,
  getWorkingConversationCount,
} from "../../store/conversation.tsx";

export const TOP_BAR_HEIGHT = 54;

interface TopBarProps {
  /** What is on screen: the conversation, dashboard or page title. */
  title?: React.ReactNode;
  /** Shows the button that opens the drawer, when it is closed. */
  onOpenDrawer?: () => void;
  /** Anonymous sessions have no drawer, so the bar carries the brand. */
  showBrand?: boolean;
}

/**
 * The bar across the top of the main panel, as in the approved design: a
 * hairline-ruled strip holding the title of what is on screen. It replaces
 * the large logo that used to sit in the top right corner.
 */
const TopBar: React.FC<TopBarProps> = ({ title, onOpenDrawer, showBrand }) => {
  // Ready takes precedence over working: it is the state that needs the
  // user's attention, while working is merely informational.
  const hasReady = useTypedSelector(getHasReadyConversation);
  const hasWorking = useTypedSelector(
    (state) => getWorkingConversationCount(state) > 0,
  );
  const badgeStatus = hasReady ? "ready" : hasWorking ? "working" : null;

  return (
    <Box
      component="header"
      sx={{
        height: `${TOP_BAR_HEIGHT}px`,
        flex: `0 0 ${TOP_BAR_HEIGHT}px`,
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "0 20px",
        borderBottom: `1px solid ${tallyTokens.line}`,
        backgroundColor: `color-mix(in srgb, ${tallyTokens.canvas} 86%, transparent)`,
        backdropFilter: "blur(8px)",
        // Above the scrolling content, which sits at zIndex 1.
        position: "relative",
        zIndex: 5,
      }}
    >
      {onOpenDrawer && (
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <IconButton
            onClick={onOpenDrawer}
            aria-label={i18n._(
              msg({
                context:
                  "Aria label for the top bar button that opens the menu",
                message: "Open menu",
              }),
            )}
            sx={{
              width: "32px",
              height: "32px",
              padding: 0,
              borderRadius: "8px",
              color: tallyTokens.ink2,
              "&:hover": {
                backgroundColor: tallyTokens.surface2,
                color: tallyTokens.ink,
              },
            }}
          >
            <OpenDrawerIcon fontSize="17px" />
          </IconButton>
          {/* Visible with the drawer closed, since that's the only time this
              button renders - see the `!props.openDrawer` guard in
              ConversationPanel.tsx. */}
          {badgeStatus && (
            <Box
              sx={{
                position: "absolute",
                top: "2px",
                right: "2px",
                pointerEvents: "none",
              }}
            >
              <ConversationActivityDot status={badgeStatus} size={7} />
            </Box>
          )}
        </Box>
      )}
      {showBrand && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <Box
            component="img"
            src={TallyMark}
            alt="Tally"
            sx={{
              height: "26px",
              width: "26px",
              borderRadius: "8px",
              display: "block",
            }}
          />
          <Box
            component="span"
            sx={{
              fontSize: "19px",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              lineHeight: 1,
              color: tallyTokens.ink,
            }}
          >
            tally
          </Box>
        </Box>
      )}
      {title && (
        <Box
          component="span"
          sx={{
            minWidth: 0,
            fontSize: "14.5px",
            fontWeight: 600,
            color: tallyTokens.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Box>
      )}
    </Box>
  );
};

export default TopBar;
